/** Wait for the burst of rotation events to collapse. */
export const REFIT_DEBOUNCE_MS = 80;
/** innerWidth and innerHeight are still the old orientation immediately after rotate. */
export const REFIT_SETTLE_MS = 320;

export interface ViewportSize {
  width: number;
  height: number;
}

export function isLandscape(width: number, height: number): boolean {
  return width > 0 && height > 0 && width > height;
}

/** Phones and tablets. A desktop window is not treated as one. */
export function isTouchDevice(nav: { maxTouchPoints?: number } | null | undefined, hasTouchEvent: boolean): boolean {
  return (nav?.maxTouchPoints ?? 0) > 0 || hasTouchEvent;
}

/**
 * Prefer the visual viewport once it has a size. Right after a rotation both
 * this and innerWidth can be stale; callers measure again after `REFIT_SETTLE_MS`.
 */
export function measureViewport(win: {
  innerWidth: number;
  innerHeight: number;
  visualViewport?: { width: number; height: number } | null;
}): ViewportSize {
  const visual = win.visualViewport;
  const width = visual && visual.width > 0 ? visual.width : win.innerWidth;
  const height = visual && visual.height > 0 ? visual.height : win.innerHeight;
  return { width, height };
}

export interface RefitHost {
  setTimeout: (fn: () => void, ms: number) => number;
  clearTimeout: (id: number) => void;
}

/**
 * One refit after the event burst, and a second once the browser has published
 * the post-rotation viewport. Returns a queue function.
 */
export function createRefitQueue(host: RefitHost, refit: () => void): () => void {
  let debounce = 0;
  let settle = 0;
  return () => {
    host.clearTimeout(debounce);
    host.clearTimeout(settle);
    debounce = host.setTimeout(() => {
      refit();
      settle = host.setTimeout(refit, REFIT_SETTLE_MS);
    }, REFIT_DEBOUNCE_MS);
  };
}

interface ScaleHost {
  scale: {
    setParentSize: (width: number, height: number) => void;
    refresh: () => void;
  };
}

/** Pixel size, so a stuck percentage height cannot leave the canvas at the landscape box. */
function pinViewport(width: number, height: number): void {
  const pxW = `${Math.round(width)}px`;
  const pxH = `${Math.round(height)}px`;
  document.documentElement.style.width = pxW;
  document.documentElement.style.height = pxH;
  document.body.style.width = pxW;
  document.body.style.height = pxH;
  const parent = document.getElementById('game');
  if (!parent) return;
  parent.style.width = pxW;
  parent.style.height = pxH;
}

/**
 * Refit the canvas when the phone rotates. The portrait layout stays as built.
 * Scale-to-fit letterboxes it in the center of a landscape window. Nothing covers it.
 */
export function installPortrait(game: ScaleHost): void {
  const apply = () => {
    const size = measureViewport(window);
    if (size.width <= 0 || size.height <= 0) return;
    pinViewport(size.width, size.height);
    game.scale.setParentSize(size.width, size.height);
    game.scale.refresh();
  };
  const queue = createRefitQueue(window, apply);
  window.addEventListener('resize', queue);
  window.addEventListener('orientationchange', queue);
  window.visualViewport?.addEventListener('resize', queue);
  apply();
}
